import { isDefined } from "../../infra3d";
import { Obj } from "../internal";
import { Feature, FeatureStyle } from "./internal";
/**
 * Representation of the FeatureCollection-Object specified in GeoJSON-Standard.
 *
 * @class FeatureCollection
 * @author Ported by Kevin Flueckiger, based on code by Benjamin Loesch
 */
export class FeatureCollection extends Obj {
    /**
     * @constructor
     *
     * @param scene
     * @param {FeatureStyle=} style The style-Object for the fc.
     * @param {FeatureStyle=} highlightstyle The highlightstyle-Object for the fc.
     * @param {string=} name The name of the fc.
     * @param {boolean=} pickable set this to false if all features in this collection shouldn't be pickable
     * @param parent
     */
    constructor(scene, style, highlightstyle, name, pickable, parent) {
        super();
        /**
         * This is the GeoJSON type. It should always be "FeatureCollection".
         * @property {string} type
         */
        this.type = "FeatureCollection";
        /**
         * The Array of features that this Collection is managing.
         * @property {Array.<Feature>} features
         */
        this.features = [];
        this.scene = scene;
        this.infrascene = scene.infrascene;
        this.parentfeaturehandler = parent;
        const defaultstyle = new FeatureStyle({
            stylename: "default",
            rules: [
                {
                    filter: "",
                    symbolizer: "point",
                    params: {
                        size: 8,
                        stroke: [1, 1, 0, 1],
                    },
                },
                {
                    filter: "",
                    symbolizer: "line",
                    params: {
                        "stroke-width": 1,
                        stroke: [1, 1, 0, 1],
                    },
                },
                {
                    filter: "",
                    symbolizer: "polygon",
                    params: {
                        fill: [1, 0, 0, 1],
                    },
                },
            ],
        });
        this.style = style || defaultstyle;
        const defaulthighlightstyle = new FeatureStyle({
            stylename: "highlightstyle",
            rules: [
                {
                    filter: "",
                    symbolizer: "point",
                    params: {
                        size: 10,
                        stroke: [1, 0.404, 0, 1],
                    },
                },
                {
                    filter: "",
                    symbolizer: "line",
                    params: {
                        "stroke-width": 1,
                        stroke: [1, 0.404, 0, 1],
                    },
                },
                {
                    filter: "",
                    symbolizer: "polygon",
                    params: {
                        fill: [1, 0.404, 0, 0.6],
                    },
                },
            ],
        });
        this.highlightstyle = highlightstyle || defaulthighlightstyle;
        this.name = name || "myFeatureCollection";
        if (isDefined(pickable)) {
            this.pickable = pickable;
        }
        else {
            this.pickable = true;
        }
    }
    /**
     * Creates a new empty Feature object.
     *
     * @return {Feature}
     */
    createFeature() {
        const feature = new Feature(this);
        this.features.push(feature);
        return feature;
    }
    /**
     * Parses the FeatureCollection content from a GeoJSON Object
     *
     * @param {Object} fc
     */
    parseFromJSON(fc) {
        if (fc["type"] == "FeatureCollection") {
            for (let i = 0; i < fc["features"].length; i++) {
                const f = fc["features"][i];
                const new_feature = this.createFeature();
                new_feature.parseFromJSON(f);
            }
            // todo: Parse the EPSG-Code. Gehört auch zum GeoJSON-Standard. Sollte aber nur geparst werden, wenn
            // todo: dann auch die Features ins Zielsystem (EPSG-System des Streams) transformiert werden können.
            if (isDefined(fc["crs"])) {
                // todo: parse EPSG-Code
            }
            else {
                // todo: Use the EPSG-Code of the Stream, if one is defined, otherwise use 21781 (LV03)
            }
        }
    }
    /**
     * Returns a GeoJSON string containing all features in the FeatureCollection
     *
     * @return {string}
     */
    toGeoJsonString() {
        let jsonstring = '{ "type" : ' + '"FeatureCollection",';
        jsonstring += '"features" : [';
        for (let i = 0; i < this.features.length; i++) {
            jsonstring += this.features[i].toGeoJsonString();
            if (i < this.features.length - 1)
                jsonstring += ",";
        }
        jsonstring += "]";
        jsonstring += this._getEPSGNodeString();
        jsonstring += "}";
        return jsonstring;
    }
    /**
     * Destroys the Feature Collection
     */
    destroy() {
        for (let i = this.features.length - 1; i > -1; i--) {
            this.destroyFeature(this.features[i]);
        }
        this.infrascene.render();
    }
    /**
     * Destroys a Feature in the collection
     *
     * @param {Feature} feature The Feature to destroy.
     * @param {boolean?} needsrender
     */
    destroyFeature(feature, needsrender = false) {
        let index = -1;
        for (let i = 0; i < this.features.length; i++) {
            if (this.features[i] == feature) {
                this.features[i].destroy(needsrender);
                this.features[i] = null;
                index = i;
            }
        }
        if (index >= 0)
            this.features.splice(index, 1);
    }
    /**
     * @description Adds an existing Feature to the FeatureCollection and applies the style definitions from the FeatureCollection to the Feature.
     *
     * @param {Feature} feature
     * @param {boolean?} needsrender
     */
    addFeature(feature, needsrender = false) {
        const newfeature = this.createFeature();
        const str = feature.toGeoJsonString();
        const a = JSON.parse(str);
        newfeature.parseFromJSON(a);
        if (needsrender)
            this.infrascene.render();
        return newfeature;
    }
    /**
     * Adds an existing Feature to the FeatureCollection from geojson
     * @method addFeatureFromGeoJSON
     * @param {Object} feature
     * @param {boolean?} needsrender
     */
    addFeatureFromGeoJSON(feature, needsrender = false) {
        const newfeature = this.createFeature();
        newfeature.parseFromJSON(feature);
        if (needsrender) {
            //Needs double render call in canvas-mode. Why?
            this.infrascene.render();
            this.infrascene.render();
        }
        return newfeature;
    }
    /**
     * Adds an existing Feature to the FeatureCollection from geojson
     * @method addFeatureFromGeoJSONString
     * @param {string} feature
     * @param {boolean?} needsrender
     */
    addFeatureFromGeoJSONString(feature, needsrender = false) {
        const a = JSON.parse(feature);
        return this.addFeatureFromGeoJSON(a, needsrender);
    }
    /**
     * Copies the FeatureCollection
     *
     * @return {FeatureCollection} Returns a Copy of this FeatureCollection
     */
    copy() {
        const newfeaturecollection = new FeatureCollection(this.scene, this.style, this.highlightstyle, this.name, this.pickable, this.parentfeaturehandler);
        const str = this.toGeoJsonString();
        const a = JSON.parse(str);
        newfeaturecollection.parseFromJSON(a);
        return newfeaturecollection;
    }
    /**
     * Sets a new Style and/or Highlightstyle for the FeatureCollection
     *
     * @param {FeatureStyle=} style Optional. The Style-Object
     * @param {FeatureStyle=} highlightstyle Optional. The Highlightstyle-Object
     */
    changeStyle(style, highlightstyle) {
        this.style = style || this.style;
        this.highlightstyle = highlightstyle || this.highlightstyle;
        for (let i = 0; i < this.features.length; i++) {
            this.features[i].changeStyle(this.style, this.highlightstyle);
        }
        this.update();
        this.infrascene.render();
    }
    /**
     * Updates the Featurecollection
     */
    update() {
        for (let i = 0; i < this.features.length; i++) {
            this.features[i].update(false);
        }
        this.infrascene.render();
    }
    /**
     * Shows all Features in the collection
     */
    show() {
        for (let i = 0; i < this.features.length; i++) {
            this.features[i].show();
        }
        this.infrascene.render();
    }
    /**
     * Hides all Features in the collection
     */
    hide() {
        for (let i = 0; i < this.features.length; i++) {
            this.features[i].hide();
        }
        this.infrascene.render();
    }
    /**
     * highlight all features in collection
     */
    highlightOn() {
        for (let i = 0; i < this.features.length; i++) {
            this.features[i].highlightOn();
        }
        this.infrascene.render();
    }
    /**
     * unhighlight all features in collection
     */
    highlightOff() {
        for (let i = 0; i < this.features.length; i++) {
            this.features[i].highlightOff();
        }
        this.infrascene.render();
    }
    /**
     * picks all objects in collection and pushs picked features into pickedfeaturearray
     * @param mx
     * @param my
     * @param pickeddataarray
     */
    pick(mx, my, pickeddataarray) {
        for (let i = 0; i < this.features.length; i++) {
            this.features[i].pick(mx, my, pickeddataarray);
        }
    }
    getName() {
        return this.name;
    }
    /**
     * Returns the crs-Part (EPSG-Code) for a geojson-string. It uses the epsg-code of the stream if its defined or a default epsg-code (21781, LV03).
     *
     * @return {string}
     */
    _getEPSGNodeString() {
        const geoframe = this.parentfeaturehandler.parentscene.getCurrentGeoframe();
        if (geoframe != null) {
            return ',"crs": {"type": "name","properties": {"name": "EPSG:' + geoframe.srid + '"}}';
        }
        else {
            return ',"crs": {"type": "name","properties": {"name": "EPSG:21781"}}'; //default value
        }
    }
}
//# sourceMappingURL=featurecollection.js.map